import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import AdapterDateFns from '@date-io/date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent
} from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { AppState, store, useAppSelector } from 'src/store';
import { setSnackbarMessage } from 'src/store/snackbarMessage';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import DocManagementService from '../documentUpload/service/DocManagementService';
import { AddtoTaskProps, ITask } from '../documentViewer/types';

const AddtoTasksDialogComponent: FC<AddtoTaskProps> = (props: AddtoTaskProps) => {
  const { openAddToTaskDialog, setOpenAddToTaskDialog, actionName } = props;
  const actionText = actionName === 'Send For Review' ? 'review' : 'approval';
  const [isProcessing, setProcessing] = useState<boolean>(false);
  const docService = new DocManagementService();
  const { userPreferedDateFormat, preferedFormatForDatePicker } = usePreferedDateFormat();
  const [value, setValue] = React.useState<string | Date | null>(null);
  const [usersList, setUsersList] = React.useState<string[]>([]);
  const [isValidUsers, setValidUsers] = React.useState<boolean>(true);
  const [selectedUsersList, setSelectedUsersList] = React.useState<string[]>([]);
  const [openConfirmExit, setOpenConfirmExit] = useState(false);
  const [priority, setPriority] = React.useState<string | null | any>('');
  const [taskTitle, setTaskTitle] = useState<string | null | any>('');
  const documentDetail: any = useSelector((state: AppState) => state.documentDetail);
  const user: any = useAppSelector(state => state.user.user);
  const userName = `${user.firstName + ' ' + user.lastName}`;
  const [errorUploadedDueData, setErrorUploadedDueData] = useState<string>('');
  const isfLevel = documentDetail?.data[0]?.isf_doc_level;
  const selectedStudy = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Study);
  const selectedCountry = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Country);
  const selectedSite = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Site);
  const [defaultUser, setDefaultUser] = React.useState<string | null | any>([
    {
      user_id: user.id,
      user_name: user?.firstName + ' ' + user?.lastName,
      user_email_id: user.email
    }
  ]);

  React.useEffect(() => {
    getAllUsers();
  }, []);

  const getAllUsers = async () => {
    setProcessing(true);
    const response: any = await docService.getAllUsers(
      selectedStudy,
      isfLevel,
      documentDetail.data[0].isf_artifact_name,
      selectedCountry,
      selectedSite
    );
    const filtered = response.data.filter(function (el: any) {
      return el.firstName !== null;
    });
    if (response?.data) {
      setUsersList(filtered);
    }
    setProcessing(false);
  };

  const createTask = async (docData: ITask) => {
    setProcessing(true);
    const res: any = await docService.createTask(
      documentDetail.data[0]?.isf_document_id,
      docData,
      false,
      documentDetail.data[0]?.isf_doc_study,
      documentDetail.data[0]?.isf_doc_country,
      documentDetail.data[0]?.isf_doc_site,
      documentDetail.data[0]?.isf_doc_level,
      process.env.REACT_APP_NAME,
      'DocumentTask',
      taskTitle
    );
    setProcessing(false);
    if (res.data) {
      store.dispatch(setSnackbarMessage('Task added successfully.', 'success'));
      setOpenAddToTaskDialog(false);
      setOpenConfirmExit(false);
    } else if (taskTitle === null || value === null || priority === null || selectedUsersList === null) {
      store.dispatch(setSnackbarMessage('Mandatory field missing .', 'error'));
    }
  };

  const handleChange = (event: SelectChangeEvent) => {
    setPriority(event.target.value as string);
  };

  const handleTaskTitle = (e: any) => {
    setTaskTitle(e.target.value);
  };

  const handleClose = () => {
    if (taskTitle || value || priority || selectedUsersList?.length) {
      setOpenConfirmExit(true);
    } else {
      setOpenAddToTaskDialog(false);
      setOpenConfirmExit(false);
    }
  };

  const handleAddtask = () => {
    if (handleInvalidDate()) return;
    const users: any = [];
    selectedUsersList.map((item: any) => {
      return users.push({
        user_id: item.id,
        user_name: item?.firstName + ' ' + item?.lastName,
        user_email_id: item.email
      });
    });
    const docData: ITask = {
      users: users ? users : defaultUser,
      action_name: 'create task',
      isf_doc_status: documentDetail.data[0]?.isf_doc_status,
      task_id: 'test',
      task_title: taskTitle,
      due_date: value !== null ? new Date(value) : '',
      priority: priority,
      created_by: userName,
      created_by_email: user.email
    };
    createTask(docData);
  };

  const handleInvalidDueDate = (err: any, value: any) => {
    if (err === null) setErrorUploadedDueData('');
    if (err === 'minDate') {
      setErrorUploadedDueData('Due data can not be past date');
    } else if (err === 'disablePast') {
      setErrorUploadedDueData('Past dates are not allowed');
    } else if (err === 'invalidDate') {
      setErrorUploadedDueData('Invalid date format');
    }
  };

  const handleInvalidDate = () => {
    if (errorUploadedDueData !== '') {
      store.dispatch(setSnackbarMessage(errorUploadedDueData, 'error'));
      return true;
    }
  };

  return (
    <>
      <PopupComponent open={openAddToTaskDialog} className="senddata-modal modalwithDatePicker">
        <DialogTitle>Add to Tasks</DialogTitle>
        {isProcessing && <LinearProgress />}
        <DialogContent className="senddata-modalcontent-new">
          <FormControl fullWidth>
            <TextField
              disabled={isProcessing}
              id="addtask-title"
              label="Task Title"
              variant="standard"
              required
              inputProps={{
                maxLength: 250
              }}
              onChange={e => handleTaskTitle(e)}
              value={taskTitle.trimStart()}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mt: 3 }}>
            <Autocomplete
              disabled={isProcessing}
              multiple
              id="tags-standard"
              options={usersList}
              getOptionLabel={(option: any) => option?.firstName + ' ' + option?.lastName + ' (' + option?.email + ')'}
              renderTags={(value: readonly any[], getTagProps) =>
                value.map((option: any, index: number) => (
                  <div key={index}>
                    <Chip
                      variant="outlined"
                      label={option?.firstName + ' ' + option?.lastName || option}
                      {...getTagProps({ index })}
                    />
                  </div>
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  variant="standard"
                  label="Select user(s) to review"
                  placeholder="Users"
                  disabled={isProcessing}
                  required
                />
              )}
              onChange={(event, newValue) => {
                setSelectedUsersList(newValue);
              }}
            />
            {!isValidUsers && <Alert severity="error">Entered user is not valid.</Alert>}
          </FormControl>
          <FormControl fullWidth sx={{ mt: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} variant="standard" className="custom-datePicker">
              <DesktopDatePicker
                disabled={isProcessing}
                disablePast
                inputFormat={userPreferedDateFormat}
                label={
                  <span>
                    Due Date <span style={{ color: 'red', fontSize: '12px' }}>*</span>
                  </span>
                }
                value={preferedFormatForDatePicker(value)}
                onError={(err: any, value: any) => handleInvalidDueDate(err, value)}
                minDate={value}
                onChange={(newValue: any) => {
                  if (newValue !== null) {
                    setValue(new Date(newValue).toLocaleDateString('en-CA'));
                  }
                }}
                renderInput={(params: any) => (
                  <TextField {...params} variant="standard" InputLabelProps={{ shrink: true }} />
                )}
              />
            </LocalizationProvider>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 3 }} variant="standard">
            <InputLabel id="priority-select-label">
              Priority <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            </InputLabel>
            <Select
              disabled={isProcessing}
              labelId="priority-select-label"
              id="priority-select"
              value={priority}
              label="Priority"
              onChange={handleChange}
            >
              <MenuItem value={'Low'}>Low</MenuItem>
              <MenuItem value={'Medium'}>Medium</MenuItem>
              <MenuItem value={'High'}>High</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button variant="text" className="grey-btn" onClick={handleClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            disabled={
              !taskTitle || !value || !priority || !selectedUsersList || !selectedUsersList?.length || isProcessing
            }
            onClick={handleAddtask}
          >
            Add
          </Button>
        </DialogActions>
      </PopupComponent>
      <div>
        <PopupComponent
          open={openConfirmExit}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="confirm-modal"
          child={true}
        >
          <DialogTitle id="alert-dialog-title">
            <b>Please Confirm</b>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              All unsaved changes will be lost. Are you sure you want to exit before saving your changes?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenConfirmExit(false);
              }}
              className="grey-btn"
            >
              CLOSE
            </Button>
            <Button
              onClick={() => {
                setOpenAddToTaskDialog(false);
                setOpenConfirmExit(false);
              }}
            >
              EXIT
            </Button>
          </DialogActions>
        </PopupComponent>
      </div>
    </>
  );
};

export default React.memo(AddtoTasksDialogComponent);
